import React from 'react';
import { CognitoUser } from './UserManagement';

interface UserManagementTableProps {
  users: CognitoUser[];
  onEnable: (username: string) => void;
  onDisable: (username: string) => void;
  onDelete: (username: string) => void;
}

const UserManagementTable: React.FC<UserManagementTableProps> = ({
  users,
  onEnable,
  onDisable,
  onDelete
}) => {
  const getStatusBadge = (user: CognitoUser) => {
    if (!user.enabled) {
      return {
        text: 'Disabled',
        backgroundColor: '#fef2f2',
        color: '#991b1b',
        borderColor: '#fecaca'
      };
    }
    if (user.status === 'FORCE_CHANGE_PASSWORD') {
      return {
        text: 'Pending Password',
        backgroundColor: '#fefce8',
        color: '#854d0e',
        borderColor: '#fde047'
      };
    }
    if (user.status === 'CONFIRMED') {
      return {
        text: 'Active',
        backgroundColor: '#f0fdf4',
        color: '#166534',
        borderColor: '#86efac'
      };
    }
    return {
      text: user.status,
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderColor: '#d1d5db'
    };
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (users.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
          No users found
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Create a new user or adjust your search filters.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      overflow: 'hidden'
    }}>
      {/* Table Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 120px 100px 150px',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        padding: '0.75rem 1rem',
        fontSize: '0.75rem',
        fontWeight: '600',
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        <div>Email</div>
        <div>Name</div>
        <div>Status</div>
        <div>Created</div>
        <div>Actions</div>
      </div>

      {/* Table Body */}
      {users.map((user, index) => {
        const statusBadge = getStatusBadge(user);
        return (
          <div
            key={user.username}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 120px 100px 150px',
              borderBottom: index < users.length - 1 ? '1px solid #f1f5f9' : 'none',
              padding: '0.75rem 1rem',
              alignItems: 'center',
              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
            }}
          >
            {/* Email */}
            <div style={{
              fontSize: '0.875rem',
              color: '#111827',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user.email || user.username}
            </div>

            {/* Name */}
            <div style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user.name || '-'}
            </div>

            {/* Status */}
            <div>
              <span style={{
                display: 'inline-block',
                padding: '0.25rem 0.5rem',
                backgroundColor: statusBadge.backgroundColor,
                color: statusBadge.color,
                border: `1px solid ${statusBadge.borderColor}`,
                borderRadius: '0.25rem',
                fontSize: '0.625rem',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                {statusBadge.text}
              </span>
            </div>

            {/* Created Date */}
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280'
            }}>
              {formatDate(user.created_at)}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {user.enabled ? (
                <button
                  onClick={() => onDisable(user.username)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#fefce8',
                    color: '#854d0e',
                    border: '1px solid #fde047',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.625rem',
                    fontWeight: '600'
                  }}
                >
                  Disable
                </button>
              ) : (
                <button
                  onClick={() => onEnable(user.username)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#f0fdf4',
                    color: '#166534',
                    border: '1px solid #86efac',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.625rem',
                    fontWeight: '600'
                  }}
                >
                  Enable
                </button>
              )}
              <button
                onClick={() => onDelete(user.username)}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: '0.625rem',
                  fontWeight: '600'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserManagementTable;
